import { observe, toJS } from 'mobx';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { Prompt, withRouter } from 'react-router-dom';
import { Button, Col, Form, FormGroup, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { EButton, EInput } from 'src/Components/EInput';
import { CalcStore } from 'src/Stores/Calc/CalcStore';
import { TestsStore } from 'src/Stores/Tests/TestsStore';
import DeepCopy from 'src/UtilityFunctions/DeepCopy';
import { IDrillString, IDrillStringItemType, ITest } from '../../../../../Interfaces/TestInterface';
import { IWell } from '../../../../../Interfaces/WellInterface';
import { decimalPlaces } from '../../../../../UtilityFunctions/DecimalPlaces';
import DataTable from '../../../../DataTable';
import Footer from '../../../../Footer';
import WorkflowHeader from '../../../WorkflowHeader';

interface IList {
    headers: any[];
    rows: any[];
}


@inject('stores')
@observer
class DrillString extends React.Component {
    public props: any;
    private dataSet: IList;
    private test: ITest = new ITest();
    public TestsStore: TestsStore;
    private testDisposer: any;
    private well: IWell = new IWell();
    public WellsStore: any;
    private wellDisposer: any;
    private drillStringItem: IDrillString = new IDrillString();
    private CalcStore: CalcStore;
    private headerRow: any[];

    public state = {
        dataSet: this.dataSet,
        drillStringItem: this.drillStringItem,
        drillStringItemTypes: [] as IDrillStringItemType[],
        drillStringToolDescription: [] as string[],
        editing: false,
        isDirty: false,
        modal: false,
        rowIndex: 0,
        saveClicked: false,
        test: this.test,
        well: this.well
    };

    constructor(props: any) {
        super(props);

        this.CalcStore = this.props.stores.CalcStore;
        this.TestsStore = this.props.stores.TestsStore;
        this.WellsStore = this.props.stores.WellsStore;
        this.populateHeaderRow();
    }

    public componentDidMount() {
        this.createDisposers();
        this.getDrillStringItemTypes();
        this.getToolsDescriptionOptions();
        if ( this.props.stores.WellsStore.well && this.props.stores.TestsStore.test ) {
            this.setState({
                test: toJS(this.props.stores.TestsStore.test),
                well: toJS(this.props.stores.WellsStore.well)
            }, () => {
                this.formatDataTable();
            });
        } else {
            if ( ! this.TestsStore.test ) {
                this.TestsStore.getTestDetails(this.props.match.params.taskId, this.props.match.params.wellId);
            }
            if ( ! this.WellsStore.well ) {
                this.WellsStore.getWellDetails(this.props.match.params.wellId);
            }
        }
    }

    public createDisposers() {
        this.testDisposer = observe(this.TestsStore, 'test', (change: any) => {
            const test = toJS(change.newValue);
            this.setState({
                test
            }, () => {
                this.formatDataTable();
            });
        });
        this.wellDisposer = observe(this.WellsStore, 'well', (change: any) => {
            const well = toJS(change.newValue);
            this.setState({
                well
            });
        });
    }

    private populateHeaderRow = () => {
        this.headerRow = [
            {
                cls: 'bold-table-header',
                content: 'Bottom of Hole',
            },
        ];
    }
    private formatDataTable() {
        const newDataList: any[] = [];
        const tableHeaders = [
            'Item Type',
            'Item Look-Up',
            'Description',
            'OD (in)',
            'ID (in)',
            'Length (ft)'
        ];
        if ( this.state.test.drillStringAssembly ) {
            this.state.test.drillStringAssembly.map((item: IDrillString, index: number) => {
                const newObject = [
                    {
                        content: item.itemType
                    },
                    {
                        content: item.lookUpAvailable
                    },
                    {
                        content: item.description
                    },
                    {
                        content: item.outerDiameter
                    },
                    {
                        content: item.innerDiameter
                    },
                    {
                        content: item.length
                    }
                ];
                newDataList.push(newObject);
            });
        }
        const listStructure = {
            headers: tableHeaders,
            rows: newDataList
        };
        this.setState({
            dataSet: listStructure
        });
    }

    public async componentWillUnmount() {
        this.clearDisposers();
    }

    public clearDisposers() {
        if ( this.testDisposer ) {
            this.testDisposer();
        }
        if ( this.wellDisposer ) {
            this.wellDisposer();
        }
    }

    private getDrillStringItemTypes() {
        this.TestsStore.getDrillStringItemTypes()
        .then(
            (data: any) => {
                this.setState({
                    drillStringItemTypes: data
                });
            }
        );
    }

    private getToolsDescriptionOptions() {
        this.TestsStore.getToolsDescriptionOptions()
        .then(
            data => {
                this.setState({
                    drillStringToolDescription: data,
                });
            }
        );
    }

    private handleDelete = (index: number) => {
        const test = this.state.test;
        const drillStringAssembly = test.drillStringAssembly;
        drillStringAssembly.splice(index,1);
        test.drillStringAssembly = drillStringAssembly;
        this.setState({
            isDirty: true,
            test
        }, () => {
            this.formatDataTable();
        });
    }

    private handleEdit = (index: number) => {
        this.setState({
            drillStringItem: DeepCopy(this.state.test.drillStringAssembly[index]),
            editing: true,
            rowIndex: index
        });
        this.toggleModal();
    }

    private handleCancel = () => {
        this.setState({
            drillStringItem: new IDrillString(),
            editing: false,
        });
        this.toggleModal();
    }

    private updateDrillStringCalcs = (drillStringItem: IDrillString): void => {
        const drillStringItemCap = this.CalcStore.capacityInBarrelsPerFoot({
            innerDiameter: drillStringItem.innerDiameter,
            length: drillStringItem.length,
        });
        drillStringItem.cap = Number(drillStringItemCap.toFixed(4));
        const drillStringItemDisp = this.CalcStore.displacementInBarrelsPerFoot({
            innerDiameter: drillStringItem.innerDiameter,
            length: drillStringItem.length,
            outerDiameter: drillStringItem.outerDiameter,
        });
        drillStringItem.disp = Number(drillStringItemDisp.toFixed(4));
    }

    private handleSave = (addAnother: boolean) => () => {
        const test = this.state.test;
        if ( ! this.state.editing ) {
            this.updateDrillStringCalcs(this.state.drillStringItem);
            test.drillStringAssembly.push(this.state.drillStringItem);
        } else {
            this.updateDrillStringCalcs(test.drillStringAssembly[this.state.rowIndex]);
        }
        this.setState({
            drillStringItem: new IDrillString(),
            editing: false,
            isDirty: true,
            test,
        });
        this.formatDataTable();
        if (!addAnother) {
            this.toggleModal();
        }
    }

    private handleReorder = (startIndex: any, endIndex: any) => {
        const test = this.state.test;
        const result = test.drillStringAssembly;
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);
        this.setState({
            isDirty: true,
            test
        }, () => {
            this.formatDataTable();
        });
    }

    private calcDrillStringLength = () => {
        return this.state.test.drillStringAssembly.reduce((total: number, currItem) => {
            return total + Number(currItem.length);
        }, 0);
    }
    private toggleModal = () => {
        if ( this.state.modal ) {
            this.setState({
                drillStringItem: new IDrillString(),
                editing: false,
                modal: !this.state.modal
            });
        } else {
            this.setState({
                modal: !this.state.modal
            });
        }
    }

    private handleInputChange = (event: any) => {
        if ( ! this.state.editing ) {
            const drillStringItem = this.state.drillStringItem;
            drillStringItem[event.target.name] = event.target.value;
            this.setState({
                drillStringItem
            });
        } else {
            const drillStringItem = this.state.test.drillStringAssembly[this.state.rowIndex];
            drillStringItem[event.target.name] = event.target.value;
            this.setState({
                drillStringItem
            });
        }
    }

    private checkDecimalPlaces = (value: any, maxDecimal: number) => () => {
        return decimalPlaces(value) > maxDecimal;
    }

    private checkInnerLessThanOuter = () => {
        const drillStringItem = this.state.editing ? this.state.test.drillStringAssembly[this.state.rowIndex] : this.state.drillStringItem;
        if (Number(drillStringItem.innerDiameter) !== 0 && Number(drillStringItem.outerDiameter) !== 0) {
            return Number(drillStringItem.innerDiameter) >= Number(drillStringItem.outerDiameter);
        }
        return false;
    }
    private disableSave = () => {
        return this.checkDecimalPlaces(!this.state.editing ?
            this.state.drillStringItem.outerDiameter :
            this.state.test.drillStringAssembly[this.state.rowIndex].outerDiameter, 3)()
            ||
            this.checkDecimalPlaces(!this.state.editing ?
                this.state.drillStringItem.innerDiameter :
                this.state.test.drillStringAssembly[this.state.rowIndex].innerDiameter, 3)()
            || this.checkInnerLessThanOuter();
    }

    private handleDropdownChange = (event: any) => {
        const itemName = event.target.value;
        const drillStringItem = this.state.drillStringItem;
        const item = this.state.drillStringItemTypes.find(dsit => dsit.name === itemName);
        if (item) {
            drillStringItem.itemTypeShortName = item.shortName;
            if (item.description) {
                drillStringItem.description = item.description;
            }
        }
        drillStringItem.itemType = itemName;
        this.setState({
            drillStringItem,
        });
    }

    private addOrdinals = (ordinalList: any[]) => {
        ordinalList.forEach((value: any, index: any) => {
            value.ordinal = index;
        });
    }
    private updateTestModel = () => {
        const areYouSure = confirm('Please check your Drill String Assembly for accuracy so that all items are in the proper order with their correct lengths.\n\nPress OK to save and continue.');
        if (areYouSure) {
            this.addOrdinals(this.state.test.drillStringAssembly);
            this.TestsStore.updateTestModel(this.state.test, this.props.match.params.wellId).then((test:any) => {
                if (test) {
                    this.setState({
                        isDirty: false,
                    });
                }
            });
        }
    }

    private buttonNavigation = (event: any) => {
        const $target = event.target;

        if ( $target.name === 'previous' ) {
            this.props.history.push('/well/' + this.props.match.params.wellId + '/test/' + this.props.match.params.taskId + '/holegeometry/bitdata');
        } else {
            this.props.history.push('/well/' + this.props.match.params.wellId + '/test/' + this.props.match.params.taskId + '/holegeometry/casingandinterval');
        }
    }

    private getDifferenceTextColor = (lengthDiff: number) => {
        let differenceTextColor = '';
        switch (true) {
            case lengthDiff > 0: {
                differenceTextColor = 'blue';
                break;
            }
            case lengthDiff < 0: {
                differenceTextColor = 'red';
                break;
            }
            default: {
                differenceTextColor = 'black';
            }
        }
        return differenceTextColor;
    }
    public render() {
        const disableWellStatus = this.WellsStore.well && this.WellsStore.well.wellStatus !== 'Active';
        const drillStringLength = this.calcDrillStringLength();
        const lengthDifference = this.state.test.depthBit - drillStringLength;
        const diffTextColor = this.getDifferenceTextColor(lengthDifference);
        return(
            <div className="DrillString">
            <Prompt when={this.state.isDirty} message={"Changes have been made.\n\nPress Cancel then Press Save OR Press OK to continue without saving."} />
                <WorkflowHeader workflowHeaderItems={this.props.workflowHeaderItems} isInventoryReport={this.state.test.isInventoryReport}/>
                <h5>Bit Depth: {this.state.test.depthBit} ft</h5>
                <h5>Drill String Length: { drillStringLength } ft</h5>
                <h5 style={{color: diffTextColor}}>Difference: {lengthDifference} ft</h5>
                <DataTable
                    headerButtons={this.headerRow}
                    list={this.state.dataSet}
                    onDelete={this.handleDelete}
                    onEdit={this.handleEdit}
                    onNew={{
                        callback: this.toggleModal,
                        text: 'Add Drill String Item'
                    }}
                    onReorder={this.handleReorder}
                    disableEditing={disableWellStatus}
                />
                <h5 style={{color: 'red', textAlign: 'right'}}>Drill String item lengths will be truncated or removed if the length total entered is greater than the Well Test’s Bit Depth.</h5>
                <Footer well={this.WellsStore.well} test={this.TestsStore.test} onSave={this.updateTestModel} onNavigate={this.buttonNavigation} />
                <Modal isOpen={this.state.modal} fade={false} toggle={this.toggleModal}>
                    <ModalHeader toggle={this.toggleModal}>{ this.state.editing ? 'Edit Drill String Item' : 'Add New Drill String Item' }</ModalHeader>
                    <ModalBody>
                        <Form>
                            <Row>
                                <Col md="6">
                                    <FormGroup className="required">
                                        <Label>Item Type</Label>
                                        <EInput
                                            type="select"
                                            name="itemType"
                                            id="itemType"
                                            required={true}
                                            value={ this.state.drillStringItem.itemType}
                                            onChange={this.handleDropdownChange}
                                        >
                                            <option selected={true}/>
                                            { this.state.drillStringItemTypes && this.state.drillStringItemTypes.map(itemType =>
                                                <option key={itemType.id} value={itemType.name}>{itemType.name}</option>
                                            ) }
                                        </EInput>
                                    </FormGroup>
                                </Col>
                                
                            
                                    <Col md="6">
                                    <FormGroup className="required">
                                        <Label>Description</Label>
                                        { (!this.state.editing ? 
                                    this.state.drillStringItem.itemType !== 'Tools'
                                    : this.state.test.drillStringAssembly[this.state.rowIndex].itemType !== 'Tools')
                                    ?
                                        <EInput
                                            type="text"
                                            name="description"
                                            id="description"
                                            value={this.state.drillStringItem.description}
                                                onChange={this.handleInputChange}
                                            disabled={this.state.drillStringItem.itemType === '' ||
                                                this.state.drillStringItem.itemType !== 'Miscellaneous'
                                            }
                                         />
                                    :
                                        <EInput
                                            type="select"
                                            name="description"
                                            id="description"
                                            onChange={this.handleInputChange}
                                            required={true}
                                            value={this.state.drillStringItem.description}
                                        >
                                            <option selected={true}/>
                                            {
                                                this.state.drillStringToolDescription.map((toolDesc, index) => 
                                                    <option key={index} value={toolDesc}>{toolDesc}</option>
                                                )
                                            }
                                        </EInput>
                                    }
                                    </FormGroup>
                                </Col>
                                <Col md="4">
                                    <FormGroup className="required">
                                        <Label>OD (in)</Label>
                                        <EInput
                                            type="text"
                                            name="outerDiameter"
                                            id="outerDiameter"
                                            data-validation="decimal"
                                            required={true}
                                            value={this.state.drillStringItem.outerDiameter}
                                            onChange={this.handleInputChange}
                                            errorCallback={[{
                                                callback: this.checkDecimalPlaces(!this.state.editing ?
                                                    this.state.drillStringItem.outerDiameter :
                                                    this.state.test.drillStringAssembly[this.state.rowIndex].outerDiameter, 3),
                                                message: 'Value has a maximum of 3 decimal places.'
                                            },
                                            {
                                                callback: this.checkInnerLessThanOuter,
                                                message: 'Inner Diameter must be less than Outer Diameter.'
                                            }
                                            ]}
                                         />
                                    </FormGroup>
                                </Col>
                                <Col md="4">
                                    <FormGroup className="required">
                                        <Label>ID (in)</Label>
                                        <EInput
                                            type="text"
                                            name="innerDiameter"
                                            id="innerDiameter"
                                            data-validation="decimal"
                                            required={true}
                                            value={this.state.drillStringItem.innerDiameter}
                                            onChange={this.handleInputChange}
                                            errorCallback={[{
                                                callback: this.checkDecimalPlaces(this.state.drillStringItem.innerDiameter, 3),
                                                message: 'Value has a maximum of 3 decimal places.'
                                            },
                                            {
                                                callback: this.checkInnerLessThanOuter,
                                                message: 'Inner Diameter must be less than Outer Diameter.'
                                            }
                                            ]}
                                         />
                                    </FormGroup>
                                </Col>
                                <Col md="4">
                                    <FormGroup className="required">
                                        <Label>Length (ft)</Label>
                                        <EInput
                                            type="text"
                                            name="length"
                                            id="length"
                                            data-validation="integer"
                                            required={true}
                                            value={this.state.drillStringItem.length}
                                            onChange={this.handleInputChange}
                                         />
                                    </FormGroup>
                                </Col>
                            </Row>
                        </Form>
                    </ModalBody>
                    <ModalFooter>
                        <Button className="secondary" onClick={this.handleCancel}>Cancel</Button>
                        <EButton color="primary" disabled={this.disableSave()} onClick={this.handleSave(false)}>Update</EButton>
                        {
                            !this.state.editing &&
                            <EButton color="primary" disabled={this.disableSave()} onClick={this.handleSave(true)}>Add Another</EButton>
                        }
                    </ModalFooter>
                </Modal>
            </div>
        );
    }
}

export default withRouter(DrillString);



// WEBPACK FOOTER //
// ./src/Components/Main/TestWorkflow/HoleGeometry/DrillString/DrillString.tsx